import { ShieldCheck, Clock, Eye, FileCheck2 } from "lucide-react";
import { verificationStatusLabel } from "@/lib/agora/editorial-status";
import type { Database } from "@/integrations/supabase/types";

type Status = Database["public"]["Enums"]["agora_verification_status"];

/**
 * Statut de vérification expliqué. Un contributeur doit comprendre, sans nous
 * écrire, ce qui est contrôlé, sous quel délai, ce qu'il peut faire en
 * attendant et qui voit son profil pendant l'instruction.
 * Le libellé provient de `verificationStatusLabel` afin qu'il soit strictement
 * identique à celui du résumé du tableau de bord.
 */
export default function VerificationStatusCard({ status }: { status: Status }) {
  if (status === "verified") {
    return (
      <div className="mt-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-4">
        <p className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
          <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden />
          {verificationStatusLabel(status)}
        </p>
        <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
          La mention « auteur vérifié » apparaît sous chacune de vos analyses publiées. Signalez-nous
          tout changement de structure ou d'inscription au barreau afin de la maintenir à jour.
        </p>
      </div>
    );
  }



  return (
    <div className="mt-4 rounded-lg border border-border bg-muted/40 p-4">
      <p className="inline-flex items-center gap-2 text-sm font-medium text-foreground">
        <Clock className="h-4 w-4 shrink-0 text-primary" aria-hidden />
        {verificationStatusLabel(status)}
      </p>


      <dl className="mt-3 space-y-2.5 text-xs leading-relaxed">
        <div>
          <dt className="inline-flex items-center gap-1.5 font-medium text-foreground">
            <FileCheck2 className="h-3.5 w-3.5 shrink-0" aria-hidden /> Ce qui est contrôlé
          </dt>
          <dd className="mt-0.5 text-muted-foreground">
            Votre identité, votre qualité déclarée (barreau, juridiction, direction juridique,
            université) et le rattachement à la structure indiquée.
          </dd>
        </div>
        <div>
          <dt className="inline-flex items-center gap-1.5 font-medium text-foreground">
            <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden /> Quand intervient le contrôle
          </dt>
          <dd className="mt-0.5 text-muted-foreground">
            Il est réalisé manuellement par la rédaction, à l'occasion de votre première analyse
            soumise à la revue juridique. Aucun délai n'est garanti ; vous êtes prévenu dès que la
            décision est prise.
          </dd>
        </div>

        <div>
          <dt className="inline-flex items-center gap-1.5 font-medium text-foreground">
            <FileCheck2 className="h-3.5 w-3.5 shrink-0" aria-hidden /> Possible dès maintenant
          </dt>
          <dd className="mt-0.5 text-muted-foreground">
            Rédiger, sourcer et soumettre vos analyses : aucune règle ne subordonne la soumission à
            la vérification. La mise en ligne relève ensuite de la décision de la revue juridique,
            qui apprécie librement chaque analyse ; la mention « auteur vérifié », elle, dépend
            uniquement de ce contrôle de qualité professionnelle.
          </dd>
        </div>

        <div>
          <dt className="inline-flex items-center gap-1.5 font-medium text-foreground">
            <Eye className="h-3.5 w-3.5 shrink-0" aria-hidden /> Visibilité du profil
          </dt>
          <dd className="mt-0.5 text-muted-foreground">
            Votre page d'auteur n'est visible publiquement qu'à partir de votre première analyse
            publiée ; d'ici là, seuls vous et la rédaction y avez accès.
          </dd>
        </div>
      </dl>
    </div>
  );
}
